'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type FirestoreError,
  type QuerySnapshot,
} from 'firebase/firestore';
import { ArrowUpRight, Newspaper } from 'lucide-react';

import { db } from '@/lib/firebase/config';
import type { NewsItem } from '@/types/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { HomeHeader } from '@/components/home/home-header';
import { Skeleton } from '@/components/ui/skeleton';

function formatDate(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
}

export default function NewsIndexPage() {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const handleSnapshot = useCallback((snap: QuerySnapshot<DocumentData>) => {
    setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as NewsItem[]);
    setLoading(false);
  }, []);

  const handleSnapshotError = useCallback((error: FirestoreError) => {
    console.error('Error loading news:', error);
    if (error.code === 'failed-precondition') {
      setLoadError('Firestore index required. Deploy indexes then refresh.');
    } else {
      setLoadError('Failed to load AI Newsletter preview. Check console for details.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, 'news'),
      where('published', '==', true),
      orderBy('publishedAt', 'desc'),
      limit(6)
    );

    const unsub = onSnapshot(q, handleSnapshot, handleSnapshotError);

    return () => unsub();
  }, [handleSnapshot, handleSnapshotError]);

  return (
    <div className="min-h-screen">
      <HomeHeader />

      <main className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-3">
              <Newspaper className="h-5 w-5 text-primary" />
              <h1 className="text-4xl font-bold tracking-tight">AI Newsletter</h1>
            </div>
            <p className="text-muted-foreground text-lg">
              Curated AI updates and important releases. Open any item to read a short preview.
            </p>
          </div>

          <div className="mt-10">
            {loadError ? (
              <Card className="border-2">
                <CardHeader>
                  <CardTitle>AI Newsletter not loading</CardTitle>
                  <CardDescription>{loadError}</CardDescription>
                </CardHeader>
              </Card>
            ) : loading ? (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <Card key={idx} className="border-2">
                    <CardHeader className="space-y-2">
                      <Skeleton className="h-5 w-4/5" />
                      <Skeleton className="h-4 w-full" />
                    </CardHeader>
                  </Card>
                ))}
              </div>
            ) : items.length === 0 ? (
              <Card className="border-2">
                <CardHeader>
                  <CardTitle>No items yet</CardTitle>
                  <CardDescription>Add AI news in the admin panel to publish it here.</CardDescription>
                </CardHeader>
              </Card>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {items.map((item) => (
                  <Card key={item.id} className="border-2 hover:border-primary/50 transition-all hover:shadow-lg">
                    {item.coverImage ? (
                      <div className="relative w-full h-44">
                        <Image
                          src={item.coverImage}
                          alt={item.title}
                          fill
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          className="object-cover rounded-t-lg"
                        />
                      </div>
                    ) : null}
                    <CardHeader className="space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <Badge variant="outline" className="text-xs">
                          {item.source}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{formatDate(item.publishedAt)}</span>
                      </div>
                      <CardTitle className="text-xl line-clamp-2">{item.title}</CardTitle>
                      <CardDescription className="line-clamp-3">{item.summary}</CardDescription>

                      {item.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {item.tags.slice(0, 4).map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardHeader>
                    <CardContent className="flex items-center justify-between gap-3">
                      <Link href={`/news/${encodeURIComponent(item.id)}`} className="w-full">
                        <Button variant="outline" size="sm" className="w-full">
                          Read preview
                          <ArrowUpRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
